import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { updateSchedaSpese } from "../../../features/schedaSpese/schedaSpeseSlice";

/**
 * Custom hook for managing scheda sharing functionality
 * @param {Object} scheda - The scheda object
 * @param {Function} onRefresh - Callback to refresh data after update
 * @param {Function} onClose - Callback to close the modal
 */
export const useSchedaShare = (scheda, onRefresh, onClose) => {
  const dispatch = useDispatch();

  const [sharedUserUpdateForm, setSharedUserUpdateForm] = useState({
    titolo: scheda.titolo,
    utentiList: [],
    removedEmails: [],
    roleUpdates: [],
  });

  const [isFormModified, setIsFormModified] = useState(false);

  const { utentiList, roleUpdates } = sharedUserUpdateForm;

  const removeSharedUser = (userMail) => {
    setSharedUserUpdateForm((f) => ({
      ...f,
      removedEmails: [
        ...f.removedEmails,
        { mail: userMail.email, mailId: userMail._id },
      ],
      roleUpdates: f.roleUpdates.filter((r) => r.mailId !== userMail._id),
    }));
    setIsFormModified(true);
  };

  const changeUserRole = (userMail, role) => {
    setSharedUserUpdateForm((f) => ({
      ...f,
      roleUpdates: [
        ...f.roleUpdates.filter((r) => r.mailId !== userMail._id),
        { mailId: userMail._id, role },
      ],
    }));
    setIsFormModified(true);
  };

  const getPendingRole = (mailId) =>
    roleUpdates.find((r) => r.mailId === mailId)?.role;

  const addEmail = (newEmailEntry) => {
    setSharedUserUpdateForm((f) => ({
      ...f,
      utentiList: [...f.utentiList, newEmailEntry],
    }));
    setIsFormModified(true);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const removeEmail = (email) => {
    setSharedUserUpdateForm((prev) => ({
      ...prev,
      utentiList: prev.utentiList.filter(
        (entry) => entry.email !== email
      ),
    }));
  };

  const resetForm = () => {
    setSharedUserUpdateForm({
      titolo: scheda.titolo,
      utentiList: [],
      removedEmails: [],
      roleUpdates: [],
    });
    setIsFormModified(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updatePayload = {
      titolo: sharedUserUpdateForm.titolo,
      utentiList: sharedUserUpdateForm.utentiList,
      removedEmails: sharedUserUpdateForm.removedEmails,
      roleUpdates: sharedUserUpdateForm.roleUpdates,
    };

    try {
      await dispatch(
        updateSchedaSpese({ schedaId: scheda._id, ...updatePayload })
      ).unwrap();

      if (updatePayload.utentiList.length > 0) {
        toast.success(`Scheda "${scheda.titolo}" condivisa con successo!`);
      }
      if (updatePayload.removedEmails.length > 0) {
        toast.success(`Utenti rimossi con successo!`);
      }
      if (updatePayload.roleUpdates.length > 0) {
        toast.success(`Ruoli aggiornati con successo!`);
      }

      if (onRefresh) {
        await onRefresh();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      toast.error(`Error updating shared users: ${error.message}`);
    }
  };

  return {
    utentiList,
    isFormModified,
    removeSharedUser,
    changeUserRole,
    getPendingRole,
    addEmail,
    validateEmail,
    removeEmail,
    resetForm,
    handleSubmit,
  };
};

export default useSchedaShare;
